import React from 'react';
import { ArrowTrendingUpIcon, ArrowTrendingDownIcon } from '@heroicons/react/24/outline';
import VehicleThumbnail from '../shared/VehicleThumbnail';

interface PricingSummaryProps {
  vehicle: {
    vehicle_id: string;
    vin: string;
    year: number;
    make: string;
    model: string;
    asking_price?: number;
    wholesale_value?: number;
    trade_in_value?: number;
    retail_value?: number;
    acquisition_cost?: number;
    total_cost?: number;
    estimated_profit?: number;
    profit_margin?: number;
    days_on_lot?: number;
  };
  onAdjustPrice?: (vehicle: PricingSummaryProps['vehicle']) => void;
  onViewDetails?: (vehicle: PricingSummaryProps['vehicle']) => void;
}

const PricingSummaryCardCompact: React.FC<PricingSummaryProps> = ({
  vehicle,
  onAdjustPrice,
  onViewDetails,
}) => {
  const formatCurrency = (amount?: number) => {
    if (!amount) return 'N/A';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatPercentage = (percentage?: number) => {
    if (!percentage) return 'N/A';
    return `${percentage.toFixed(1)}%`;
  };

  const getProfitColor = (profit?: number) => {
    if (!profit) return 'text-gray-500';
    return profit > 0 ? 'text-green-600' : 'text-red-600';
  };

  const getProfitIcon = (profit?: number) => {
    if (!profit) return null;
    return profit > 0 ? ArrowTrendingUpIcon : ArrowTrendingDownIcon;
  };

  // Simplify model name - take only the first 3 words to avoid overly long names
  const simplifyModel = (model: string) => {
    const words = model.split(' ');
    return words.slice(0, 2).join(' '); // Take first 2 words for more compact display
  };

  const ProfitIcon = getProfitIcon(vehicle.estimated_profit);

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-3 hover:shadow-md transition-shadow">
      {/* Header with thumbnail and title */}
      <div className="flex items-center space-x-3 mb-3">
        <VehicleThumbnail
          vehicleId={vehicle.vehicle_id}
          year={vehicle.year}
          make={vehicle.make}
          model={vehicle.model}
          size="small"
        />
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-semibold text-gray-900 truncate">
            {vehicle.year} {vehicle.make} {simplifyModel(vehicle.model)}
          </h3>
          <p className="text-xs text-gray-500 truncate">VIN: {vehicle.vin.slice(-8)}</p>
        </div>
      </div>

      {/* Asking Price - Prominent */}
      <div className="text-center mb-3">
        <p className="text-xl font-bold text-gray-900">{formatCurrency(vehicle.asking_price)}</p>
        <p className="text-xs text-gray-500">Asking Price</p>
      </div>

      {/* Compact metrics in a single row */}
      <div className="space-y-1 mb-3 text-xs">
        <div className="flex justify-between">
          <span className="text-gray-600">Cost:</span>
          <span className="font-medium">
            {formatCurrency(vehicle.total_cost || vehicle.acquisition_cost)}
          </span>
        </div>
        <div className="flex justify-between">
          <span className="text-gray-600">Wholesale:</span>
          <span className="font-medium">{formatCurrency(vehicle.wholesale_value)}</span>
        </div>
      </div>

      {/* Profit highlight */}
      <div className="border-t pt-2 mb-3">
        <div className="flex items-center justify-between text-xs">
          <span className="text-gray-600">Profit:</span>
          <div className="flex items-center space-x-1">
            {ProfitIcon && (
              <ProfitIcon className={`w-3 h-3 ${getProfitColor(vehicle.estimated_profit)}`} />
            )}
            <span className={`font-semibold ${getProfitColor(vehicle.estimated_profit)}`}>
              {formatCurrency(vehicle.estimated_profit)}
            </span>
            <span className={`${getProfitColor(vehicle.estimated_profit)}`}>
              ({formatPercentage(vehicle.profit_margin)})
            </span>
          </div>
        </div>
      </div>

      {/* Days on lot */}
      {vehicle.days_on_lot !== undefined && (
        <div className="flex justify-between items-center text-xs mb-3">
          <span className="text-gray-600">Days:</span>
          <span
            className={`font-medium ${
              vehicle.days_on_lot > 90
                ? 'text-red-600'
                : vehicle.days_on_lot > 60
                  ? 'text-yellow-600'
                  : 'text-green-600'
            }`}
          >
            {vehicle.days_on_lot}
          </span>
        </div>
      )}

      {/* Compact action buttons */}
      <div className="flex space-x-1">
        <button
          onClick={() => onAdjustPrice?.(vehicle)}
          className="flex-1 px-2 py-1 text-xs bg-blue-50 text-blue-700 rounded hover:bg-blue-100 transition-colors"
        >
          Adjust
        </button>
        <button
          onClick={() => onViewDetails?.(vehicle)}
          className="flex-1 px-2 py-1 text-xs bg-gray-50 text-gray-700 rounded hover:bg-gray-100 transition-colors"
        >
          Details
        </button>
      </div>
    </div>
  );
};

export default PricingSummaryCardCompact;
